import React, { useState, useEffect, useRef } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, Wind, Thermometer, MapPin, RefreshCw, CloudSun, CloudFog } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';

interface WeatherData {
  temperature: number;
  weatherCode: number;
  windSpeed: number;
  humidity: number;
  isDay: boolean;
  locationName?: string;
  cachedAt?: number;
}

// Weather code to description and icon mapping
const getWeatherInfo = (code: number, isDay: boolean) => {
  // WMO Weather interpretation codes
  const weatherCodes: Record<number, { description: string; icon: React.ReactNode; color: string }> = {
    0: { description: 'Clear sky', icon: <Sun size={40} />, color: 'text-yellow-500' },
    1: { description: 'Mainly clear', icon: isDay ? <Sun size={40} /> : <Cloud size={40} />, color: 'text-yellow-400' },
    2: { description: 'Partly cloudy', icon: <CloudSun size={40} />, color: 'text-blue-400' },
    3: { description: 'Overcast', icon: <Cloud size={40} />, color: 'text-gray-500' },
    45: { description: 'Foggy', icon: <CloudFog size={40} />, color: 'text-gray-400' },
    48: { description: 'Rime fog', icon: <CloudFog size={40} />, color: 'text-gray-400' },
    51: { description: 'Light drizzle', icon: <CloudRain size={40} />, color: 'text-blue-400' },
    53: { description: 'Drizzle', icon: <CloudRain size={40} />, color: 'text-blue-500' },
    55: { description: 'Dense drizzle', icon: <CloudRain size={40} />, color: 'text-blue-600' },
    61: { description: 'Light rain', icon: <CloudRain size={40} />, color: 'text-blue-400' },
    63: { description: 'Rain', icon: <CloudRain size={40} />, color: 'text-blue-500' },
    65: { description: 'Heavy rain', icon: <CloudRain size={40} />, color: 'text-blue-700' },
    71: { description: 'Light snow', icon: <CloudSnow size={40} />, color: 'text-cyan-300' },
    73: { description: 'Snow', icon: <CloudSnow size={40} />, color: 'text-cyan-400' },
    75: { description: 'Heavy snow', icon: <CloudSnow size={40} />, color: 'text-cyan-500' },
    80: { description: 'Rain showers', icon: <CloudRain size={40} />, color: 'text-blue-500' },
    81: { description: 'Moderate showers', icon: <CloudRain size={40} />, color: 'text-blue-600' },
    82: { description: 'Heavy showers', icon: <CloudRain size={40} />, color: 'text-blue-700' },
    85: { description: 'Snow showers', icon: <CloudSnow size={40} />, color: 'text-cyan-400' },
    86: { description: 'Heavy snow showers', icon: <CloudSnow size={40} />, color: 'text-cyan-500' },
    95: { description: 'Thunderstorm', icon: <CloudRain size={40} />, color: 'text-purple-500' },
  };
  
  return weatherCodes[code] || { description: 'Unknown', icon: <Cloud size={40} />, color: 'text-gray-500' };
};

// Cache key and duration (15 minutes)
const CACHE_KEY = 'dipps_weather_cache';
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in ms

export const WeatherWidget = () => {
  const { t } = useLanguage();
  const [weather, setWeather] = useState<WeatherData | null>(() => {
    // INSTANT LOAD: Try to load from cache immediately
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached) as WeatherData;
        // Return cached data immediately (we'll refresh in background if stale)
        return data;
      }
    } catch {
      // Cache read failed, continue with null
    }
    return null;
  });
  const [loading, setLoading] = useState(!weather); // Only show loading if no cached data
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unit, setUnit] = useState<'C' | 'F'>(() => {
    return (localStorage.getItem('weather_unit') as 'C' | 'F') || 'C';
  });
  const fetchInProgress = useRef(false);

  const fetchWeather = async (lat: number, lon: number, isBackgroundRefresh = false) => {
    // Prevent multiple simultaneous fetches
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;
    
    try {
      if (!isBackgroundRefresh) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);
      
      // Fetch weather from Open-Meteo (free, no API key required)
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m,is_day&timezone=auto`
      );
      
      if (!response.ok) throw new Error('Weather fetch failed');
      
      const data = await response.json();
      
      // Use saved location name or skip the slow geocoding call
      const savedLocation = localStorage.getItem('weather_location_name') || '';
      
      const weatherData: WeatherData = {
        temperature: data.current.temperature_2m,
        weatherCode: data.current.weather_code,
        windSpeed: data.current.wind_speed_10m,
        humidity: data.current.relative_humidity_2m,
        isDay: data.current.is_day === 1,
        locationName: savedLocation,
        cachedAt: Date.now(),
      };
      
      // Save to cache
      localStorage.setItem(CACHE_KEY, JSON.stringify(weatherData));
      setWeather(weatherData);
      
      // Fetch location name in background only if we don't have one saved
      if (!savedLocation) {
        fetchLocationName(lat, lon);
      }
    } catch (err) {
      if (!isBackgroundRefresh && !weather) {
        setError(t('weatherError') || 'Unable to load weather');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      fetchInProgress.current = false;
    }
  };
  
  // Fetch location name separately (non-blocking)
  const fetchLocationName = async (lat: number, lon: number) => {
    try {
      const geoResponse = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10`
      );
      if (geoResponse.ok) {
        const geoData = await geoResponse.json();
        const locationName = geoData.address?.city || geoData.address?.town || geoData.address?.county || '';
        if (locationName) {
          localStorage.setItem('weather_location_name', locationName);
          setWeather(prev => prev ? { ...prev, locationName } : prev);
          // Update cache with location name
          const cached = localStorage.getItem(CACHE_KEY);
          if (cached) {
            const data = JSON.parse(cached);
            data.locationName = locationName;
            localStorage.setItem(CACHE_KEY, JSON.stringify(data));
          }
        }
      }
    } catch {
      // Silently fail - location name is optional
    }
  };

  const getLocation = (forceRefresh = false) => {
    // Check if we have valid cached data
    if (!forceRefresh && weather?.cachedAt) {
      const age = Date.now() - weather.cachedAt;
      if (age < CACHE_DURATION) {
        // Cache is fresh, trigger background refresh only
        const savedLat = localStorage.getItem('weather_lat');
        const savedLon = localStorage.getItem('weather_lon');
        if (savedLat && savedLon) {
          fetchWeather(parseFloat(savedLat), parseFloat(savedLon), true);
        }
        return;
      }
    }
    
    // Use saved coordinates if available (much faster than geolocation)
    const savedLat = localStorage.getItem('weather_lat');
    const savedLon = localStorage.getItem('weather_lon');
    
    if (savedLat && savedLon && !forceRefresh) {
      fetchWeather(parseFloat(savedLat), parseFloat(savedLon));
      return;
    }
    
    // Only ask for geolocation if no saved location
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          localStorage.setItem('weather_lat', latitude.toString());
          localStorage.setItem('weather_lon', longitude.toString());
          fetchWeather(latitude, longitude);
        },
        () => {
          // Use saved location or default (Vancouver, BC)
          if (savedLat && savedLon) {
            fetchWeather(parseFloat(savedLat), parseFloat(savedLon));
          } else {
            // Default: Vancouver, BC
            localStorage.setItem('weather_lat', '49.2827');
            localStorage.setItem('weather_lon', '-123.1207');
            fetchWeather(49.2827, -123.1207);
          }
        },
        { timeout: 5000, maximumAge: 600000 } // 5s timeout, accept 10 minute old position
      );
    } else {
      // Default location
      fetchWeather(49.2827, -123.1207);
    }
  };

  useEffect(() => {
    getLocation();
    // Refresh weather every 30 minutes
    const interval = setInterval(() => getLocation(false), 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const toggleUnit = () => {
    const newUnit = unit === 'C' ? 'F' : 'C';
    setUnit(newUnit);
    localStorage.setItem('weather_unit', newUnit);
  };

  const convertTemp = (celsius: number) => {
    if (unit === 'F') {
      return Math.round((celsius * 9/5) + 32);
    }
    return Math.round(celsius);
  };

  // Show loading only if no cached data
  if (loading && !weather) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-4 text-white shadow-warm"
      >
        <div className="flex items-center justify-center gap-2">
          <RefreshCw size={20} className="animate-spin" />
          <span className="text-sm">{t('loading') || 'Loading...'}</span>
        </div>
      </motion.div>
    );
  }

  if (error && !weather) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gradient-to-br from-primary/80 to-secondary/80 rounded-2xl p-4 text-white shadow-warm"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sun size={28} className="text-yellow-200" />
            <div>
              <p className="font-medium text-sm">Weather Unavailable</p>
              <p className="text-xs opacity-80">Tap to try again</p>
            </div>
          </div>
          <button onClick={() => getLocation(true)} className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors">
            <RefreshCw size={18} />
          </button>
        </div>
      </motion.div>
    );
  }

  if (!weather) return null;

  const weatherInfo = getWeatherInfo(weather.weatherCode, weather.isDay);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${weather.isDay ? 'from-blue-400 to-cyan-500' : 'from-indigo-600 to-purple-700'} rounded-2xl p-4 text-white shadow-warm relative overflow-hidden`}
    >
      {/* Refresh indicator */}
      {refreshing && (
        <div className="absolute top-2 right-2">
          <RefreshCw size={14} className="animate-spin opacity-60" />
        </div>
      )}
      
      <div className="relative z-10">
        {/* Compact header with location */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <MapPin size={14} />
            <span className="text-xs font-medium truncate max-w-[120px]">
              {weather.locationName || (t('yourLocation') || 'Your Location')}
            </span>
          </div>
          <button 
            onClick={() => getLocation(true)} 
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
            title={t('refresh') || 'Refresh'}
            disabled={refreshing}
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Compact main weather display */}
        <div className="flex items-center gap-3">
          <div className={weatherInfo.color}>
            {weatherInfo.icon}
          </div>
          <div className="flex-1">
            <button 
              onClick={toggleUnit}
              className="text-3xl font-bold hover:opacity-80 transition-opacity"
              title={t('tapToChangeUnit') || 'Tap to change unit'}
            >
              {convertTemp(weather.temperature)}°{unit}
            </button>
            <p className="text-xs opacity-90">{weatherInfo.description}</p>
          </div>
          
          {/* Compact additional info */}
          <div className="text-xs space-y-1 text-right">
            <div className="flex items-center gap-1 justify-end">
              <Wind size={12} />
              <span>{Math.round(weather.windSpeed)}km/h</span>
            </div>
            <div className="flex items-center gap-1 justify-end">
              <Thermometer size={12} />
              <span>{weather.humidity}%</span>
            </div>
          </div>
        </div>

        {/* Compact weather tip */}
        <div className="mt-2 pt-2 border-t border-white/20 text-xs opacity-90">
          {weather.temperature < 5 ? (
            <p>🧣 {t('weatherTipCold') || 'Bundle up, it\'s chilly!'}</p>
          ) : weather.temperature > 30 ? (
            <p>💧 {t('weatherTipHot') || 'Stay hydrated today!'}</p>
          ) : weather.weatherCode >= 61 && weather.weatherCode <= 67 ? (
            <p>☔ {t('weatherTipRain') || 'Don\'t forget your umbrella!'}</p>
          ) : weather.weatherCode >= 71 && weather.weatherCode <= 77 ? (
            <p>❄️ {t('weatherTipSnow') || 'Watch for slippery surfaces!'}</p>
          ) : (
            <p>☀️ {t('weatherTipNice') || 'Great day to go outside!'}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};
